import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bowfast/ui/components/card";
import { Input } from "@bowfast/ui/components/input";
import { Label } from "@bowfast/ui/components/label";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@bowfast/ui/components/sheet";
import { motion } from "framer-motion";
import { AlertTriangleIcon, CheckCircle2Icon, CreditCardIcon, PlusIcon, Trash2Icon, UsersIcon } from "lucide-react";
import { useState } from "react";
import * as z from "zod";
import { StatTile } from "~/components/shared/StatTile";
import type { ExpeditionStatus, RecordExpenseInput } from "~/graphql/generated";
import { fadeUp, stagger } from "~/lib/animations";
import { CURRENCIES } from "~/lib/expedition-constants";
import { fmtPrice } from "~/lib/fmt";
import type { ExpeditionExpense } from "~/pages/expeditions/expeditions.types";

// Derive currency from first expense; fall back to EUR.
// TODO(backend/expedition): add `currency` + `budget` fields to Expedition type.

const addExpenseSchema = z.object({
  description: z.string().min(2, "Describe the expense"),
  amountStr: z.string().regex(/^\d+(\.\d{1,2})?$/, "Enter a valid amount (e.g. 42.50)"),
  currency: z.string().length(3),
});

function isSettled(expense: ExpeditionExpense): boolean {
  if (expense.splits.length === 0) return false;
  return expense.splits.every((s) => s.settledAt != null);
}

export function ExpensesTab({
  expenses,
  status,
  crewCount,
  onAdd,
  onDelete,
  onSettle,
  isMutating,
}: Readonly<{
  expenses: ExpeditionExpense[];
  status: ExpeditionStatus;
  crewCount: number;
  onAdd?: (input: RecordExpenseInput) => void;
  onDelete?: (id: string) => void;
  onSettle?: (expenseId: string) => void;
  isMutating?: boolean;
}>) {
  const currency = expenses[0]?.currency ?? "EUR";
  const totalExpenses = expenses.reduce((s, e) => s + e.amountCents / 100, 0);
  const settled = expenses.filter(isSettled);
  const unsettled = expenses.filter((e) => !isSettled(e));
  const canAdd = (status === "OPEN" || status === "ACTIVE") && !!onAdd;

  // ── Add expense form state ──────────────────────────────────────────────────
  const [description, setDescription] = useState("");
  const [amountStr, setAmountStr] = useState("");
  const [formCurrency, setFormCurrency] = useState(currency);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  function handleAdd() {
    const result = addExpenseSchema.safeParse({ description, amountStr, currency: formCurrency });
    if (!result.success) {
      setFormErr(result.error.issues[0]?.message ?? "Invalid input");
      return;
    }
    setFormErr(null);
    onAdd?.({
      description: description.trim(),
      amountCents: Math.round(Number.parseFloat(amountStr) * 100),
      currency: formCurrency,
    });
    setDescription("");
    setAmountStr("");
    setSheetOpen(false);
  }

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-5">
      {/* Summary stats */}
      <motion.div variants={fadeUp} className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label="Total spent"
          value={fmtPrice(totalExpenses, currency)}
          sub="across all crew"
          Icon={CreditCardIcon}
          iconColor="text-abyss-500"
          accentClass="bg-abyss-500"
        />
        <StatTile
          label="Settled"
          value={String(settled.length)}
          sub={`of ${expenses.length} expenses`}
          Icon={CheckCircle2Icon}
          iconColor="text-shallow-600"
          accentClass="bg-shallow-500"
        />
        {unsettled.length > 0 && (
          <StatTile
            label="Outstanding"
            value={String(unsettled.length)}
            sub="need settling"
            Icon={AlertTriangleIcon}
            iconColor="text-signal-600"
            accentClass="bg-signal-500"
          />
        )}
        <StatTile
          label="Per person"
          value={crewCount > 0 ? fmtPrice(totalExpenses / crewCount, currency) : "—"}
          sub="avg per crew"
          Icon={UsersIcon}
          iconColor="text-violet-500"
          accentClass="bg-violet-500"
        />
      </motion.div>

      {/* Expense list */}
      <motion.div variants={fadeUp}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <CreditCardIcon className="size-4 text-abyss-500" />
                Voyage expenses
              </CardTitle>
              {canAdd && (
                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                  <SheetTrigger render={<Button size="sm" variant="outline" className="gap-1.5" />}>
                    <PlusIcon className="size-3.5" />
                    Log expense
                  </SheetTrigger>
                  <SheetContent side="right">
                    <SheetHeader>
                      <SheetTitle>Log an expense</SheetTitle>
                    </SheetHeader>
                    <div className="flex flex-col gap-4 px-5 py-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="exp-description">Description</Label>
                        <Input
                          id="exp-description"
                          placeholder="e.g. Fuel at Porto Santo"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="exp-amount">Amount</Label>
                          <Input
                            id="exp-amount"
                            placeholder="0.00"
                            type="number"
                            min="0"
                            step="0.01"
                            value={amountStr}
                            onChange={(e) => setAmountStr(e.target.value)}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="exp-currency">Currency</Label>
                          <select
                            id="exp-currency"
                            className="field-input field-select"
                            value={formCurrency}
                            onChange={(e) => setFormCurrency(e.target.value)}
                          >
                            {CURRENCIES.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {/* TODO(backend/expense): add category selector once `category` field added */}
                      {formErr && <p className="text-xs text-destructive">{formErr}</p>}
                    </div>
                    <SheetFooter className="px-5 pb-5">
                      <SheetClose render={<Button variant="outline" />}>Cancel</SheetClose>
                      <Button onClick={handleAdd} disabled={isMutating}>
                        Log expense
                      </Button>
                    </SheetFooter>
                  </SheetContent>
                </Sheet>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {expenses.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground">
                No expenses logged yet — add the first one above.
              </p>
            ) : (
              expenses.map((expense) => {
                const settled = isSettled(expense);
                const paidBy =
                  expense.recordedBy.displayName || `${expense.recordedBy.firstName} ${expense.recordedBy.lastName}`;
                return (
                  <div
                    key={expense.id}
                    className="flex items-center gap-4 border-b border-border px-5 py-3.5 last:border-b-0 transition-colors hover:bg-muted/20"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{expense.description}</p>
                      {/* TODO(backend/expense): add `category` field for category display */}
                      <p className="text-xs text-muted-foreground">Paid by {paidBy}</p>
                    </div>
                    {!settled && onSettle && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0 text-xs"
                        disabled={isMutating}
                        onClick={() => onSettle(expense.id)}
                      >
                        Mark settled
                      </Button>
                    )}
                    <Badge variant={settled ? "confirmed" : "invited"}>{settled ? "Settled" : "Outstanding"}</Badge>
                    <span className="w-20 text-right font-heading text-sm font-bold tabular-nums">
                      {fmtPrice(expense.amountCents / 100, expense.currency)}
                    </span>
                    {onDelete && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                        disabled={isMutating}
                        onClick={() => onDelete(expense.id)}
                      >
                        <Trash2Icon className="size-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
